import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { AuthSession, AuthUser } from '@/types/auth.types'
import type { PermissionKey } from '@/types/permission.types'
import { useAuthStore } from '@/store/auth.store'

interface SessionContextValue {
  session: AuthSession | null
  user: AuthUser | null
  isAuthenticated: boolean
  isHydrated: boolean
  hasPermission: (key: PermissionKey) => boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

/**
 * SessionProvider — React context façade over the auth store.
 * Prepared for future JWT / refresh / HttpOnly cookie session hydration.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useAuthStore((s) => s.session)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isHydrated = useAuthStore((s) => s.isHydrated)
  const hasPermission = useAuthStore((s) => s.hasPermission)

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isAuthenticated,
      isHydrated,
      hasPermission,
    }),
    [session, isAuthenticated, isHydrated, hasPermission],
  )

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) {
    throw new Error('useSession must be used within SessionProvider')
  }
  return ctx
}
